// src/features/bom/pages/BomRecipeForm.tsx
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import {
    createRecipe, updateRecipe, getRecipe,
} from '../services/bomApi';
import { searchProducts, ProductResult } from '@/features/pos/services/posApi';
import { getCategories } from '@/features/products/services/productsApi';
import apiClient from '@/lib/api-client';
import {
    ArrowLeft, Save, Plus, Trash2, Search, X,
    Package, FlaskConical, ChefHat, ShoppingBag,
    Boxes, ClipboardList, Calculator, ChevronDown, ChevronUp,
    Info, CheckCircle2, DollarSign, TrendingUp,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import { extractApiError } from '@/lib/api-error';

// ============================================================
// TYPES
// ============================================================
interface IngredientItem {
    id: string;
    rawMaterialId: string;
    rawMaterialName: string;
    rawMaterialSku?: string;
    quantity: string;
    uomId: string;
    uomName: string;
    uomAbbreviation: string;
    wasteFactor: string;
    notes: string;
    costPrice: string;
    customUomName: string;
    customConversionRate: string;
    availableUoms: Array<{
        id: string;
        name: string;
        abbreviation: string;
        conversionRate?: string;
        isBaseUom: boolean;
    }>;
    isCollapsed: boolean;
}

type Step = 1 | 2 | 3;

const STEPS = [
    { id: 1, label: 'Product', icon: ShoppingBag },
    { id: 2, label: 'Ingredients', icon: Boxes },
    { id: 3, label: 'Cost & Price', icon: ClipboardList },
];

// ============================================================
// MAIN COMPONENT
// ============================================================
export function BomRecipeForm() {
    const navigate = useNavigate();
    const { recipeId } = useParams<{ recipeId: string }>();
    const isEditing = !!recipeId;
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    const [step, setStep] = useState<Step>(1);
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});

    // Form state
    const [recipeName, setRecipeName] = useState('');
    const [productName, setProductName] = useState('');
    const [categoryId, setCategoryId] = useState('');
    const [sellingPrice, setSellingPrice] = useState('');
    const [useAutoPrice, setUseAutoPrice] = useState(true);
    const [yieldQuantity, setYieldQuantity] = useState('1');
    const [yieldUomName, setYieldUomName] = useState('Portion');
    const [preparationTime, setPreparationTime] = useState('');
    const [preparationInstructions, setPreparationInstructions] = useState('');
    const [description, setDescription] = useState('');
    const [ingredients, setIngredients] = useState<IngredientItem[]>([]);

    // Ingredient modal state
    const [showIngredientModal, setShowIngredientModal] = useState(false);
    const [searchingIdx, setSearchingIdx] = useState<number | null>(null);
    const [ingredientSearch, setIngredientSearch] = useState('');

    // Load existing recipe
    const { data: existingRecipe, isLoading: loadingExisting } = useQuery({
        queryKey: ['bom-recipe-edit', recipeId],
        queryFn: () => getRecipe(user!.tenantId, recipeId!),
        enabled: isEditing && !!recipeId && !!user?.tenantId,
    });

    // Fetch product details for existing ingredients
    const { data: productDetails } = useQuery({
        queryKey: ['bom-edit-ingredient-products', existingRecipe?.items?.map((i: any) => i.rawMaterialId)],
        queryFn: async () => {
            if (!existingRecipe || !existingRecipe.items || existingRecipe.items.length === 0) return {};
            const ids = [...new Set(existingRecipe.items.map((i: any) => i.rawMaterialId))];
            const results = await Promise.all(
                ids.map(async (id) => {
                    try {
                        const { data } = await apiClient.get(`/tenants/${user!.tenantId}/products/${id}`);
                        return { id, product: data?.data };
                    } catch {
                        return { id, product: null };
                    }
                })
            );
            const map: Record<string, any> = {};
            results.forEach(({ id, product }) => {
                if (product) map[id] = product;
            });
            return map;
        },
        enabled: isEditing && !!existingRecipe && existingRecipe.items?.length > 0 && !!user?.tenantId,
    });

    // Populate form when editing
    useEffect(() => {
        if (existingRecipe) {
            setRecipeName(existingRecipe.name || '');
            setYieldQuantity(String(existingRecipe.yieldQuantity || '1'));
            setYieldUomName(existingRecipe.yieldUomName || 'Portion');
            setPreparationTime(String(existingRecipe.preparationTime || ''));
            setPreparationInstructions(existingRecipe.preparationInstructions || '');
            setDescription(existingRecipe.description || '');

            setProductName(existingRecipe.finishedProductName || '');
            setSellingPrice(String(existingRecipe.finishedProductRetailPrice || ''));
            setUseAutoPrice(!existingRecipe.finishedProductRetailPrice || parseFloat(existingRecipe.finishedProductRetailPrice) === 0);

            const initialIngredients = (existingRecipe.items || []).map((item: any, idx: number) => ({
                id: item.id || `existing-${idx}`,
                rawMaterialId: item.rawMaterialId,
                rawMaterialName: item.rawMaterialName || 'Product ' + (idx + 1),
                rawMaterialSku: item.rawMaterialSku || undefined,
                quantity: String(item.quantity || '1'),
                uomId: item.uomId || '',
                uomName: item.uomName || '',
                uomAbbreviation: item.uomAbbreviation || '',
                wasteFactor: String(item.wasteFactor || '1.0'),
                notes: item.notes || '',
                costPrice: '0',
                customUomName: item.customUomName || '',
                customConversionRate: String(item.conversionRate || ''),
                availableUoms: [],
                isCollapsed: true,
            }));
            setIngredients(initialIngredients);
        }
    }, [existingRecipe]);

    // Merge live product info
    useEffect(() => {
        if (productDetails && ingredients.length > 0) {
            setIngredients(prev => prev.map(ing => {
                const product = productDetails[ing.rawMaterialId];
                if (product) {
                    const uoms = (product.uoms || []).map((u: any) => ({
                        id: u.id,
                        name: u.name,
                        abbreviation: u.abbreviation,
                        conversionRate: String(u.conversionRate || '1'),
                        isBaseUom: Boolean(u.isBaseUom),
                    }));
                    return {
                        ...ing,
                        costPrice: (ing.costPrice === '0' && product.costPrice) ? String(product.costPrice) : ing.costPrice,
                        availableUoms: uoms.length > 0 ? uoms : ing.availableUoms,
                    };
                }
                return ing;
            }));
        }
    }, [productDetails]);

    // Categories
    const { data: categories } = useQuery({
        queryKey: ['categories-flat', user?.tenantId],
        queryFn: () => getCategories(user!.tenantId),
        enabled: !!user?.tenantId,
    });

    // Ingredient search results
    const { data: ingredientResults, isLoading: searchingProducts } = useQuery({
        queryKey: ['ingredient-search', user?.tenantId, ingredientSearch],
        queryFn: () => searchProducts(user!.tenantId, ingredientSearch),
        enabled: !!user?.tenantId && ingredientSearch.length >= 2,
    });

    // ============================================================
    // COST CALCULATION
    // ============================================================
    function calculateIngredientCost(ing: IngredientItem): number {
        const qty = parseFloat(ing.quantity) || 0;
        const waste = parseFloat(ing.wasteFactor) || 1;
        const costPerBase = parseFloat(ing.costPrice) || 0;
        let convRate = 1;
        if (ing.customConversionRate && parseFloat(ing.customConversionRate) > 0) {
            convRate = parseFloat(ing.customConversionRate);
        } else {
            const uom = ing.availableUoms.find(u => u.id === ing.uomId);
            if (uom) convRate = parseFloat(uom.conversionRate || '1') || 1;
        }
        return qty * convRate * waste * costPerBase;
    }

    const totalRecipeCost = useMemo(() => {
        return ingredients.reduce((sum, ing) => sum + calculateIngredientCost(ing), 0);
    }, [ingredients]);

    const yieldQty = parseFloat(yieldQuantity) > 0 ? parseFloat(yieldQuantity) : 1;
    const costPerYield = totalRecipeCost / yieldQty;
    const [activeMarkupPreset, setActiveMarkupPreset] = useState<number | null>(30);

    // Auto set selling price when useAutoPrice and costPerYield changes
    useEffect(() => {
        if (useAutoPrice && costPerYield > 0) {
            const suggested = costPerYield * 1.3; // 30% markup
            setSellingPrice(Math.round(suggested).toString());
        }
    }, [costPerYield, useAutoPrice]);

    const applyMarkupPreset = (pct: number) => {
        setActiveMarkupPreset(pct);
        setUseAutoPrice(false);
        if (costPerYield > 0) {
            const priceWithMarkup = costPerYield * (1 + pct / 100);
            setSellingPrice(Math.round(priceWithMarkup).toString());
        }
    };

    const formatNaira = (val: number) => `₦${val.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const profitPerYield = parseFloat(sellingPrice || '0') - costPerYield;
    const profitMargin = parseFloat(sellingPrice) > 0 ? (profitPerYield / parseFloat(sellingPrice)) * 100 : 0;
    const foodCostRatio = parseFloat(sellingPrice || '0') > 0 ? (costPerYield / parseFloat(sellingPrice)) * 100 : 0;

    // ============================================================
    // HANDLERS
    // ============================================================
    const openIngredientSearch = (idx: number) => {
        setSearchingIdx(idx);
        setIngredientSearch('');
        setShowIngredientModal(true);
    };

    const handleSelectIngredient = useCallback((product: ProductResult) => {
        if (searchingIdx === null) return;
        const baseUom = product.uoms?.find((u: any) => u.isBaseUom) || product.uoms?.[0];
        setIngredients(prev => {
            const updated = [...prev];
            updated[searchingIdx] = {
                ...updated[searchingIdx],
                rawMaterialId: product.id,
                rawMaterialName: product.name || '',
                rawMaterialSku: product.sku || undefined,
                quantity: '1',
                uomId: baseUom?.id || '',
                uomName: baseUom?.name || '',
                uomAbbreviation: baseUom?.abbreviation || '',
                costPrice: String(product.costPrice || '0'),
                customUomName: '',
                customConversionRate: '',
                availableUoms: (product.uoms || []).map((u: any) => ({
                    id: u.id,
                    name: u.name,
                    abbreviation: u.abbreviation,
                    conversionRate: String(u.conversionRate || '1'),
                    isBaseUom: Boolean(u.isBaseUom),
                })),
                isCollapsed: false,
            };
            return updated;
        });
        setShowIngredientModal(false);
        setSearchingIdx(null);
        setIngredientSearch('');
    }, [searchingIdx]);

    const addIngredient = () => {
        setIngredients(prev => [...prev, {
            id: `new-${Date.now()}`,
            rawMaterialId: '', rawMaterialName: '', rawMaterialSku: undefined,
            quantity: '1', uomId: '', uomName: '', uomAbbreviation: '',
            wasteFactor: '1.0', notes: '', costPrice: '0',
            customUomName: '', customConversionRate: '',
            availableUoms: [],
            isCollapsed: false,
        }]);
    };

    const removeIngredient = (idx: number) => setIngredients(prev => prev.filter((_, i) => i !== idx));

    const updateIngredient = (idx: number, field: keyof IngredientItem, value: string) => {
        setIngredients(prev => {
            const updated = [...prev];
            (updated[idx] as any)[field] = value;
            return updated;
        });
    };

    const toggleCollapse = (idx: number) => {
        setIngredients(prev => {
            const updated = [...prev];
            updated[idx].isCollapsed = !updated[idx].isCollapsed;
            return updated;
        });
    };

    // ============================================================
    // VALIDATION
    // ============================================================
    const validateStep1 = (): boolean => {
        const errors: Record<string, string> = {};
        if (!recipeName.trim()) errors.recipeName = 'Recipe name is required';
        if (!productName.trim()) errors.productName = 'Menu item name is required';
        if (!yieldQuantity || parseFloat(yieldQuantity) <= 0) errors.yieldQuantity = 'Yield must be greater than 0';
        if (!yieldUomName.trim()) errors.yieldUomName = 'Serving unit is required';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const validateStep2 = (): boolean => {
        if (ingredients.length === 0) {
            addToast({ type: 'error', title: 'Ingredients Required', message: 'Add at least one raw material.' });
            return false;
        }
        for (let i = 0; i < ingredients.length; i++) {
            const ing = ingredients[i];
            if (!ing.rawMaterialId) {
                addToast({ type: 'error', title: 'Missing Ingredient', message: `Select a product for item #${i + 1}.` });
                return false;
            }
            if (!ing.uomId && !(ing.customUomName && ing.customConversionRate)) {
                addToast({ type: 'error', title: 'Missing Unit', message: `Specify a UOM for ${ing.rawMaterialName}.` });
                return false;
            }
            if (!ing.quantity || parseFloat(ing.quantity) <= 0) {
                addToast({ type: 'error', title: 'Invalid Quantity', message: `Enter a valid quantity for ${ing.rawMaterialName}.` });
                return false;
            }
        }
        return true;
    };

    const validateStep3 = (): boolean => {
        if (!sellingPrice || parseFloat(sellingPrice) <= 0) {
            addToast({ type: 'error', title: 'Price Required', message: 'Please set a selling price.' });
            return false;
        }
        return true;
    };

    const goToStep = (s: Step) => {
        if (s === 2 && !validateStep1()) { setStep(1); return; }
        if (s === 3 && !validateStep2()) { setStep(2); return; }
        setStep(s);
    };

    // ============================================================
    // SUBMIT
    // ============================================================
    const saveMutation = useMutation({
        mutationFn: (data: any) =>
            isEditing ? updateRecipe(user!.tenantId, recipeId!, data) : createRecipe(user!.tenantId, data),
        onSuccess: (result: any) => {
            queryClient.invalidateQueries({ queryKey: ['bom-recipes'] });
            queryClient.invalidateQueries({ queryKey: ['products'] });
            addToast({ type: 'success', title: '🎉 Recipe Saved!', message: `"${result.name}" has been recorded.` });
            navigate('/bom');
        },
        onError: (err: any) => {
            const { title, message } = extractApiError(err, 'Save Failed');
            addToast({ type: 'error', title, message, duration: 10000 });
        },
    });

    const handleSubmit = () => {
        if (!validateStep1() || !validateStep2() || !validateStep3()) return;

        const validIngredients = ingredients.filter(i => i.rawMaterialId);
        const payload: any = {
            name: recipeName.trim(),
            description: description || undefined,
            yieldQuantity,
            yieldUomName: yieldUomName.trim(),
            preparationInstructions: preparationInstructions || undefined,
            preparationTime: preparationTime || undefined,
            items: validIngredients.map((m, idx) => ({
                rawMaterialId: m.rawMaterialId,
                quantity: m.quantity || '1',
                uomId: m.uomId || undefined,
                wasteFactor: m.wasteFactor || '1.0',
                sortOrder: String(idx),
                notes: m.notes || undefined,
                conversionRate: m.customConversionRate || undefined,
                customUomName: m.customUomName || undefined,
            })),
        };

        // Product fields
        payload.finishedProductName = productName.trim();
        if (categoryId && categoryId !== '') {
            payload.finishedProductCategoryId = categoryId;
        }
        payload.finishedProductRetailPrice = sellingPrice;

        saveMutation.mutate(payload);
    };

    if (isEditing && loadingExisting) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <Spinner size="xl" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col">
            {/* Header */}
            <div className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button onClick={() => navigate('/bom')} className="p-2 rounded-xl hover:bg-slate-100">
                            <ArrowLeft size={20} className="text-slate-500" />
                        </button>
                        <div className="p-2 rounded-xl bg-purple-600 text-white shadow-md">
                            <FlaskConical size={20} />
                        </div>
                        <div>
                            <h1 className="text-lg font-bold text-slate-900">{isEditing ? 'Edit Recipe & BOM' : 'Create Recipe & BOM'}</h1>
                            <p className="text-xs text-slate-500">Configure yield, ingredients, and pricing</p>
                        </div>
                    </div>
                    {step === 3 && (
                        <Button onClick={handleSubmit} isLoading={saveMutation.isPending} leftIcon={<Save size={16} />}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-md">
                            {isEditing ? 'Update Recipe' : 'Finalize & Save'}
                        </Button>
                    )}
                </div>
            </div>

            {/* Stepper Header */}
            <div className="bg-white border-b border-slate-200">
                <div className="max-w-4xl mx-auto px-6 py-3">
                    <div className="flex items-center justify-center gap-2">
                        {STEPS.map((s, idx) => {
                            const isActive = step === s.id;
                            const isDone = step > s.id;
                            return (
                                <div key={s.id} className="flex items-center gap-2">
                                    <button
                                        onClick={() => { if (isDone) goToStep(s.id as Step); }}
                                        disabled={!isDone && !isActive}
                                        className={clsx(
                                            'flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all',
                                            isActive && 'bg-purple-50 text-purple-700 border border-purple-200',
                                            isDone && 'bg-emerald-50 text-emerald-700 cursor-pointer',
                                            !isActive && !isDone && 'text-slate-400'
                                        )}
                                    >
                                        <div className={clsx(
                                            'w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold',
                                            isActive && 'bg-purple-600 text-white',
                                            isDone && 'bg-emerald-600 text-white',
                                            !isActive && !isDone && 'bg-slate-200 text-slate-500'
                                        )}>
                                            {isDone ? '✓' : s.id}
                                        </div>
                                        <span>{s.label}</span>
                                    </button>
                                    {idx < 2 && <div className={clsx('w-8 h-0.5 rounded', isDone ? 'bg-emerald-400' : 'bg-slate-200')} />}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Content Body */}
            <div className="flex-1 flex flex-col max-w-4xl mx-auto w-full px-6 py-6">
                <AnimatePresence mode="wait">
                    {/* STEP 1: Product Info (no selling price) */}
                    {step === 1 && (
                        <motion.div key="step1" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-6">
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-6">
                                <h2 className="text-base font-bold text-slate-800 border-b pb-3">Menu Product Information</h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <Input label="Recipe Name *" value={recipeName} onChange={(e) => setRecipeName(e.target.value)} error={formErrors.recipeName} placeholder="e.g., Smoky Jollof Rice" />
                                    <Input label="Menu Item Display Name *" value={productName} onChange={(e) => setProductName(e.target.value)} error={formErrors.productName} placeholder="e.g., Smoky Jollof Rice Portion" />
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-600 mb-1">Category</label>
                                        <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}
                                            className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-purple-500/20">
                                            <option value="">Select category...</option>
                                            {(categories || []).map((cat: any) => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                                        </select>
                                    </div>
                                    <Input label="Yield Output Quantity *" type="number" value={yieldQuantity} onChange={(e) => setYieldQuantity(e.target.value)} error={formErrors.yieldQuantity} placeholder="1" />
                                    <Input label="Yield Serving Unit *" value={yieldUomName} onChange={(e) => setYieldUomName(e.target.value)} error={formErrors.yieldUomName} placeholder="Portion, Plate, Bowl" />
                                </div>
                                <div className="border-t pt-4">
                                    <h3 className="text-sm font-semibold text-slate-500 mb-3">Additional Details (Optional)</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <Input label="Prep Time (mins)" value={preparationTime} onChange={(e) => setPreparationTime(e.target.value)} placeholder="30" />
                                        <Input label="Description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Brief description" />
                                    </div>
                                    <div className="mt-4">
                                        <label className="block text-xs font-semibold text-slate-400 mb-1">Instructions</label>
                                        <textarea value={preparationInstructions} onChange={(e) => setPreparationInstructions(e.target.value)} rows={3}
                                            className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm resize-none" placeholder="Step-by-step preparation..." />
                                    </div>
                                </div>
                            </div>
                            <div className="flex justify-end">
                                <Button onClick={() => goToStep(2)} className="bg-purple-600 hover:bg-purple-700 text-white">Next: Ingredients →</Button>
                            </div>
                        </motion.div>
                    )}

                    {/* STEP 2: Ingredients */}
                    {step === 2 && (
                        <motion.div key="step2" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-4">
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
                                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                                    <div>
                                        <h2 className="font-bold text-slate-800">Recipe Bill of Materials (BOM)</h2>
                                        <p className="text-xs text-slate-500">Add raw materials and stock items</p>
                                    </div>
                                    <Button size="sm" onClick={addIngredient} leftIcon={<Plus size={14} />} className="bg-amber-600 hover:bg-amber-700 text-white">Add Ingredient</Button>
                                </div>

                                <div className="p-4 space-y-3 max-h-[55vh] overflow-y-auto">
                                    {ingredients.length === 0 ? (
                                        <div className="text-center py-16">
                                            <Boxes size={40} className="text-slate-300 mx-auto mb-2" />
                                            <p className="text-slate-600 font-medium">No ingredients added yet</p>
                                            <p className="text-xs text-slate-400 mt-1">Add items from your inventory</p>
                                        </div>
                                    ) : (
                                        ingredients.map((ing, idx) => {
                                            const lineCost = calculateIngredientCost(ing);
                                            return (
                                                <div key={ing.id} className={clsx('rounded-xl border transition-all overflow-hidden', !ing.rawMaterialId ? 'border-dashed border-amber-300 bg-amber-50/20' : 'border-slate-200 bg-white')}>
                                                    <div className="flex items-center gap-3 p-3.5 cursor-pointer" onClick={() => toggleCollapse(idx)}>
                                                        <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600">{idx + 1}</div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="font-semibold text-sm text-slate-800 truncate">{ing.rawMaterialName || 'Click to select product...'}</p>
                                                            <p className="text-xs text-slate-500">
                                                                {ing.rawMaterialName ? `${ing.quantity} ${ing.uomAbbreviation || ing.customUomName || 'unit'} · Cost: ${formatNaira(lineCost)}` : 'No product linked'}
                                                            </p>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <button onClick={(e) => { e.stopPropagation(); removeIngredient(idx); }} className="p-1.5 text-slate-400 hover:text-rose-500"><Trash2 size={15} /></button>
                                                            {ing.isCollapsed ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronUp size={16} className="text-slate-400" />}
                                                        </div>
                                                    </div>

                                                    {!ing.isCollapsed && (
                                                        <div className="px-4 pb-4 pt-1 border-t border-slate-100 bg-slate-50/50 space-y-3">
                                                            {!ing.rawMaterialId ? (
                                                                <button onClick={(e) => { e.stopPropagation(); openIngredientSearch(idx); }}
                                                                    className="w-full px-4 py-2.5 border-2 border-dashed border-amber-300 rounded-lg text-left text-slate-500 text-sm hover:border-amber-500 bg-white flex items-center gap-2">
                                                                    <Search size={15} className="text-amber-500" /> Search product or raw material...
                                                                </button>
                                                            ) : (
                                                                <div className="flex items-center justify-between p-2.5 bg-amber-50 rounded-lg border border-amber-200">
                                                                    <div className="flex items-center gap-2">
                                                                        <Package size={16} className="text-amber-700" />
                                                                        <span className="font-semibold text-xs text-slate-800">{ing.rawMaterialName} ({ing.rawMaterialSku || 'No SKU'})</span>
                                                                    </div>
                                                                    <button onClick={() => updateIngredient(idx, 'rawMaterialId', '')} className="text-xs text-rose-600 hover:underline">Change</button>
                                                                </div>
                                                            )}

                                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                                                <Input label="Quantity" type="number" value={ing.quantity} onChange={(e) => updateIngredient(idx, 'quantity', e.target.value)} />
                                                                {ing.availableUoms.length > 0 ? (
                                                                    <div>
                                                                        <label className="block text-xs font-semibold text-slate-600 mb-1">Unit (UOM)</label>
                                                                        <select value={ing.uomId} onChange={(e) => {
                                                                            const uom = ing.availableUoms.find((u: any) => u.id === e.target.value);
                                                                            updateIngredient(idx, 'uomId', e.target.value);
                                                                            if (uom) {
                                                                                updateIngredient(idx, 'uomName', uom.name);
                                                                                updateIngredient(idx, 'uomAbbreviation', uom.abbreviation);
                                                                            }
                                                                        }} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
                                                                            <option value="">Select unit...</option>
                                                                            {ing.availableUoms.map((u: any) => (
                                                                                <option key={u.id} value={u.id}>{u.abbreviation || u.name} (1 = {u.conversionRate} base)</option>
                                                                            ))}
                                                                        </select>
                                                                    </div>
                                                                ) : (
                                                                    <Input label="Custom Unit" value={ing.customUomName} onChange={(e) => updateIngredient(idx, 'customUomName', e.target.value)} placeholder="e.g. cup" />
                                                                )}
                                                                <Input label="Waste Factor" type="number" value={ing.wasteFactor} onChange={(e) => updateIngredient(idx, 'wasteFactor', e.target.value)} />
                                                                <Input label="Base Unit Cost (₦)" type="number" value={ing.costPrice} onChange={(e) => updateIngredient(idx, 'costPrice', e.target.value)} />
                                                            </div>
                                                            {ing.availableUoms.length === 0 && (
                                                                <Input label="Custom Conversion Rate" type="number" value={ing.customConversionRate} onChange={(e) => updateIngredient(idx, 'customConversionRate', e.target.value)} placeholder="Base units per custom unit" />
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })
                                    )}
                                </div>

                                <div className="border-t border-slate-200 bg-white px-6 py-3.5 flex justify-between items-center text-sm">
                                    <span className="font-semibold text-slate-600">Calculated Batch Cost:</span>
                                    <span className="font-bold text-slate-900 text-base">{formatNaira(totalRecipeCost)}</span>
                                </div>
                            </div>

                            <div className="flex justify-between">
                                <Button variant="secondary" onClick={() => setStep(1)}>Back</Button>
                                <Button onClick={() => goToStep(3)} className="bg-purple-600 hover:bg-purple-700 text-white">Review Cost & Price →</Button>
                            </div>
                        </motion.div>
                    )}

                    {/* STEP 3: Cost & Price */}
                    {step === 3 && (
                        <motion.div key="step3" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-6">
                            {/* Cost summary */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs">
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total Batch Cost</p>
                                    <p className="text-xl font-extrabold text-slate-900 mt-1">{formatNaira(totalRecipeCost)}</p>
                                </div>
                                <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs">
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Cost per Serving</p>
                                    <p className="text-xl font-extrabold text-purple-700 mt-1">{formatNaira(costPerYield)}</p>
                                </div>
                                <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs">
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Net Profit / Serving</p>
                                    <p className={clsx('text-xl font-extrabold mt-1', profitPerYield >= 0 ? 'text-emerald-600' : 'text-rose-600')}>{formatNaira(profitPerYield)}</p>
                                </div>
                                <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs">
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Gross Profit Margin</p>
                                    <p className={clsx('text-xl font-extrabold mt-1', profitMargin >= 30 ? 'text-emerald-600' : profitMargin >= 15 ? 'text-amber-600' : 'text-rose-600')}>{profitMargin.toFixed(1)}%</p>
                                </div>
                            </div>

                            {/* Selling price & Interactive Markup Presets */}
                            <div className="bg-white rounded-3xl border border-slate-200 shadow-xs p-6 space-y-4">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-3">
                                    <div>
                                        <h3 className="font-extrabold text-slate-800 text-base">Menu Price & Markup Simulator</h3>
                                        <p className="text-xs text-slate-400">Select an industry target markup or customize your retail price</p>
                                    </div>
                                    {foodCostRatio > 0 && (
                                        <span className={clsx(
                                            'px-3 py-1 rounded-full text-xs font-bold border self-start sm:self-auto',
                                            foodCostRatio <= 32 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                            foodCostRatio <= 45 ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                            'bg-rose-50 text-rose-700 border-rose-200'
                                        )}>
                                            Food Cost: {foodCostRatio.toFixed(1)}% ({foodCostRatio <= 32 ? 'Prime' : foodCostRatio <= 45 ? 'Healthy' : 'High Cost'})
                                        </span>
                                    )}
                                </div>

                                {/* Preset Markup Chips */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Preset Margin Benchmarks</label>
                                    <div className="flex flex-wrap gap-2">
                                        {[
                                            { pct: 30, label: '+30% (Standard)' },
                                            { pct: 50, label: '+50% (Casual Dining)' },
                                            { pct: 100, label: '+100% (2x Cost - 50% Margin)' },
                                            { pct: 150, label: '+150% (2.5x Cost)' },
                                            { pct: 200, label: '+200% (3x Cost)' },
                                        ].map(preset => (
                                            <button
                                                key={preset.pct}
                                                type="button"
                                                onClick={() => applyMarkupPreset(preset.pct)}
                                                className={clsx(
                                                    'px-3 py-1.5 rounded-xl text-xs font-bold transition-all border',
                                                    activeMarkupPreset === preset.pct
                                                        ? 'bg-amber-500 text-white border-amber-500 shadow-xs'
                                                        : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-amber-50 hover:text-amber-800'
                                                )}
                                            >
                                                {preset.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end pt-2">
                                    <div>
                                        <Input
                                            label="Selling Price per Serving (₦)"
                                            type="number"
                                            value={sellingPrice}
                                            onChange={(e) => {
                                                setSellingPrice(e.target.value);
                                                setUseAutoPrice(false);
                                                setActiveMarkupPreset(null);
                                            }}
                                            error={formErrors.sellingPrice}
                                            placeholder="0.00"
                                            hint="Enter desired retail price per portion in Restaurant POS."
                                        />
                                    </div>
                                    <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-xs space-y-1">
                                        <div className="flex justify-between">
                                            <span className="text-slate-500">Projected 50 portions revenue:</span>
                                            <strong className="text-slate-900">{formatNaira((parseFloat(sellingPrice) || 0) * 50)}</strong>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-slate-500">Projected 50 portions profit:</span>
                                            <strong className="text-emerald-700 font-bold">{formatNaira(profitPerYield * 50)}</strong>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Ingredients breakdown */}
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
                                <h3 className="font-bold text-slate-800 text-sm border-b pb-2">Ingredients Breakdown</h3>
                                <div className="divide-y divide-slate-100">
                                    {ingredients.filter(i => i.rawMaterialId).map((ing) => (
                                        <div key={ing.id} className="py-2.5 flex items-center justify-between text-sm">
                                            <div>
                                                <p className="font-medium text-slate-800">{ing.rawMaterialName}</p>
                                                <p className="text-xs text-slate-400">{ing.quantity} {ing.uomAbbreviation || ing.customUomName} (Waste: {ing.wasteFactor}x)</p>
                                            </div>
                                            <span className="font-semibold text-slate-700">{formatNaira(calculateIngredientCost(ing))}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="flex justify-between">
                                <Button variant="secondary" onClick={() => setStep(2)}>Back to Ingredients</Button>
                                <Button onClick={handleSubmit} isLoading={saveMutation.isPending} leftIcon={<Save size={16} />} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                                    {isEditing ? 'Update Recipe' : 'Finalize & Save Recipe'}
                                </Button>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Ingredient Search Modal */}
            <AnimatePresence>
                {showIngredientModal && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowIngredientModal(false)}>
                        <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
                            <div className="p-4 border-b border-slate-200 flex items-center gap-3">
                                <Search size={18} className="text-slate-400" />
                                <input type="text" placeholder="Search product by name or SKU..." value={ingredientSearch} onChange={(e) => setIngredientSearch(e.target.value)}
                                    className="flex-1 px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500/20" autoFocus />
                                <button onClick={() => setShowIngredientModal(false)} className="p-1 text-slate-400 hover:text-slate-600"><X size={18} /></button>
                            </div>
                            <div className="flex-1 overflow-y-auto p-2">
                                {searchingProducts ? (
                                    <div className="flex justify-center py-8"><Spinner size="lg" /></div>
                                ) : !ingredientResults || ingredientResults.length === 0 ? (
                                    <div className="text-center py-8 text-slate-400 text-sm">No inventory items found.</div>
                                ) : (
                                    <div className="space-y-1">
                                        {ingredientResults.map((product) => (
                                            <button key={product.id} onClick={() => handleSelectIngredient(product)}
                                                className="w-full text-left px-4 py-3 rounded-xl hover:bg-purple-50 transition-colors">
                                                <p className="font-semibold text-sm text-slate-800">{product.name}</p>
                                                <p className="text-xs text-slate-400">{product.sku || 'No SKU'} · Cost: ₦{product.costPrice || '0.00'}</p>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}